/**
 * UAT release notes, "What's Changing" (Rod, 2026-11-04). Pure rules shared by
 * the Worker, the app, the loader tests and the Prentus Assistant: limits, the
 * section template, the safe text format and the section search.
 *
 * The text format is a small subset of markdown. Nothing here produces HTML:
 * the app builds React elements from the blocks, so pasted markup never
 * reaches a page as live HTML.
 *
 *   Plain line                 a paragraph (every non-empty line is one)
 *   ### Heading                a small heading inside the field
 *   - item  (or *, +, •)       a bulleted list; indent 2 spaces for a sub-item
 *   1. item                    a numbered list
 *   **bold** *italic* [text](https://link)
 *   | Area | Student |         a table; a |---|---| line after the first row
 *   |------|---------|         makes the first row the header
 */

import { parseTermsInline, termsInlineText, type TermsInline } from './terms-markdown'

export const UAT_NOTE_STATUSES = ['draft', 'published'] as const
export type UatNoteStatus = typeof UAT_NOTE_STATUSES[number]
export const UAT_NOTE_STATUS_LABEL: Record<UatNoteStatus, string> = { draft: 'Draft', published: 'Published' }

export const UAT_NOTE_LIMITS = {
  titleChars: 120,
  /** What Members See, What Changed, Student vs Jobseeker. */
  bodyChars: 20_000,
  schoolNotesChars: 10_000,
  captionChars: 300,
  notesPerProject: 60,
  imagesPerNote: 20,
  /** 10 MB per screenshot. */
  imageBytes: 10 * 1024 * 1024,
  /** Comments use the UAT note limit (2,000 characters). */
  commentChars: 2_000,
  /** Sections copied from another workspace in one request. */
  copyAtOnce: 60,
} as const

/** The template fields, in the order customers read them. */
export const UAT_NOTE_FIELDS = ['whatMembersSee', 'whatChanged', 'studentVsJobseeker', 'schoolNotes'] as const
export type UatNoteField = typeof UAT_NOTE_FIELDS[number]

/** Title Case labels. "Notes for This School" reads "Notes for <customer>" on the customer page. */
export const UAT_NOTE_FIELD_LABEL: Record<UatNoteField, string> = {
  whatMembersSee: 'What Members See',
  whatChanged: 'What Changed',
  studentVsJobseeker: 'Student vs Jobseeker',
  schoolNotes: 'Notes for This School',
}

/** Screenshot types by extension. The Worker also checks the first bytes of every image. */
export const UAT_NOTE_IMAGE_TYPES: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
}

const MIME_EXTENSION: Record<string, string> = { 'image/png': '.png', 'image/jpeg': '.jpg', 'image/webp': '.webp', 'image/gif': '.gif' }

export function isUatNoteStatus(value: unknown): value is UatNoteStatus {
  return typeof value === 'string' && (UAT_NOTE_STATUSES as readonly string[]).includes(value)
}

/** The image type from its first bytes, or null. A file name or a browser type is never trusted alone. */
export function sniffImageType(bytes: Uint8Array): string | null {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) return 'image/png'
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg'
  if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return 'image/webp'
  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38 && (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61) return 'image/gif'
  return null
}

/** A safe stored file name for a screenshot, with the extension of its real type. */
export function noteImageFileName(name: string, mimeType: string): string {
  const base = name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9._ -]/g, '').replace(/\s+/g, ' ').trim().slice(0, 160) || 'screenshot'
  return `${base}${MIME_EXTENSION[mimeType] || ''}`
}

// --- Links into the tab --------------------------------------------------------

/** The tab link to one section, for example "#uat/changes/<id>". The Assistant uses it as a source link. */
export function uatNoteHash(noteId: string): string {
  return `#uat/changes/${noteId}`
}

/** The tab link to a project's What's Changing view. */
export function uatProjectChangesHash(projectId: string): string {
  return `#uat/project/${projectId}`
}

export type UatHashTarget = { kind: 'note'; id: string } | { kind: 'project'; id: string } | { kind: 'item'; id: string } | null

const HASH_ID = /^[0-9a-zA-Z-]{1,80}$/

/** Reads "#uat/changes/<id>", "#uat/project/<id>" or "#uat/item/<id>". Anything else is null. */
export function parseUatHash(hash: string): UatHashTarget {
  const parts = hash.replace(/^#/, '').split('/')
  if (parts[0] !== 'uat' || parts.length !== 3 || !HASH_ID.test(parts[2])) return null
  if (parts[1] === 'changes') return { kind: 'note', id: parts[2] }
  if (parts[1] === 'project') return { kind: 'project', id: parts[2] }
  if (parts[1] === 'item') return { kind: 'item', id: parts[2] }
  return null
}

// --- The text format ----------------------------------------------------------

export interface NoteListItem {
  children: TermsInline[]
  /** One level of sub-items. */
  items: TermsInline[][]
}

export type NoteBlock =
  | { type: 'heading'; children: TermsInline[] }
  | { type: 'paragraph'; children: TermsInline[] }
  | { type: 'list'; ordered: boolean; start: number; items: NoteListItem[] }
  | { type: 'table'; header: TermsInline[][] | null; rows: TermsInline[][][] }

const HEADING = /^#{2,4}\s+(.*)$/
const BULLET = /^[-*+•]\s+(.*)$/
const ORDERED = /^(\d{1,9})[.)]\s+(.*)$/
const TABLE_SEPARATOR = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/
/** Rows and columns a table may have, so one pasted block cannot grow without bound. */
const TABLE_MAX = { rows: 60, columns: 8 } as const

/** Splits one table line into cells. "\|" keeps a pipe inside a cell. */
export function tableCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/(?<!\\)\|$/, '')
  const cells: string[] = []
  let current = ''
  for (let index = 0; index < trimmed.length; index += 1) {
    const char = trimmed[index]
    if (char === '\\' && trimmed[index + 1] === '|') { current += '|'; index += 1; continue }
    if (char === '|') { cells.push(current.trim()); current = ''; continue }
    current += char
  }
  cells.push(current.trim())
  return cells
}

function indentOf(raw: string): number {
  const match = raw.match(/^[ \t]*/)
  return match ? match[0].replace(/\t/g, '  ').length : 0
}

/** Parses one stored field into blocks. */
export function parseNoteText(source: string): NoteBlock[] {
  const blocks: NoteBlock[] = []
  const lines = (source || '').replace(/\r\n?/g, '\n').split('\n')
  for (let index = 0; index < lines.length; index += 1) {
    const raw = lines[index]
    const line = raw.trim()
    if (!line) continue

    // A table: the run of lines that start with a pipe.
    if (line.startsWith('|')) {
      const run: string[] = []
      while (index < lines.length && lines[index].trim().startsWith('|')) { run.push(lines[index].trim()); index += 1 }
      index -= 1
      const hasHeader = run.length > 1 && TABLE_SEPARATOR.test(run[1])
      const bodyLines = (hasHeader ? run.slice(2) : run).filter((entry) => !TABLE_SEPARATOR.test(entry)).slice(0, TABLE_MAX.rows)
      const headerCells = hasHeader ? tableCells(run[0]).slice(0, TABLE_MAX.columns) : null
      const bodyCells = bodyLines.map((entry) => tableCells(entry).slice(0, TABLE_MAX.columns))
      const width = Math.max(headerCells?.length || 0, ...bodyCells.map((cells) => cells.length))
      const pad = (cells: string[]) => [...cells, ...Array(Math.max(0, width - cells.length)).fill('')].map((cell) => parseTermsInline(cell))
      blocks.push({ type: 'table', header: headerCells ? pad(headerCells) : null, rows: bodyCells.map(pad) })
      continue
    }

    const heading = line.match(HEADING)
    if (heading) {
      const children = parseTermsInline(heading[1].trim())
      if (children.length) blocks.push({ type: 'heading', children })
      continue
    }

    const bullet = line.match(BULLET)
    const ordered = bullet ? null : line.match(ORDERED)
    if (bullet || ordered) {
      const children = parseTermsInline((bullet ? bullet[1] : ordered![2]).trim())
      const last = blocks[blocks.length - 1]
      // An indented item goes under the item above it (one level).
      if (indentOf(raw) >= 2 && last && last.type === 'list' && last.items.length) {
        last.items[last.items.length - 1].items.push(children)
        continue
      }
      if (last && last.type === 'list' && last.ordered === Boolean(ordered)) last.items.push({ children, items: [] })
      else blocks.push({ type: 'list', ordered: Boolean(ordered), start: ordered ? Number(ordered[1]) || 1 : 1, items: [{ children, items: [] }] })
      continue
    }

    const children = parseTermsInline(line)
    if (children.length) blocks.push({ type: 'paragraph', children })
  }
  return blocks
}

/** Plain text for the Assistant, search and Linear: bullets as "- ", tables as "a | b" rows. */
export function noteTextPlain(source: string): string {
  const out: string[] = []
  for (const block of parseNoteText(source)) {
    if (block.type === 'heading' || block.type === 'paragraph') out.push(termsInlineText(block.children))
    else if (block.type === 'list') {
      block.items.forEach((item, index) => {
        out.push(`${block.ordered ? `${block.start + index}.` : '-'} ${termsInlineText(item.children)}`)
        for (const sub of item.items) out.push(`  - ${termsInlineText(sub)}`)
      })
    } else {
      if (block.header) out.push(block.header.map(termsInlineText).join(' | '))
      for (const row of block.rows) out.push(row.map(termsInlineText).join(' | '))
    }
  }
  return out.join('\n')
}

export interface UatNoteText {
  title: string
  whatMembersSee: string
  whatChanged: string
  studentVsJobseeker: string
  schoolNotes: string
  captions?: string[]
}

/** One section as plain text with its field labels, for the Assistant. */
export function uatNoteSourceText(note: UatNoteText, customerLabel: string): string {
  const parts = [
    note.title,
    note.whatMembersSee.trim() ? `${UAT_NOTE_FIELD_LABEL.whatMembersSee}:\n${noteTextPlain(note.whatMembersSee)}` : '',
    note.whatChanged.trim() ? `${UAT_NOTE_FIELD_LABEL.whatChanged}:\n${noteTextPlain(note.whatChanged)}` : '',
    note.studentVsJobseeker.trim() ? `${UAT_NOTE_FIELD_LABEL.studentVsJobseeker}:\n${noteTextPlain(note.studentVsJobseeker)}` : '',
    note.schoolNotes.trim() ? `Notes for ${customerLabel}:\n${noteTextPlain(note.schoolNotes)}` : '',
    note.captions?.length ? `Screenshots:\n${note.captions.filter(Boolean).map((caption) => `- ${caption}`).join('\n')}` : '',
  ]
  return parts.filter(Boolean).join('\n\n')
}

// --- Search -----------------------------------------------------------------------

const STOP_WORDS = new Set('a an and are as at be by can do does for from has have how i if in is it its my of on or our should so that the their them then there they this to was we what when where which who why will with you your'.split(' '))

/** A simple singular: "preferences" and "preference" match, "classes" reads "class", "policies" reads "policy". */
function singular(word: string): string {
  if (word.length <= 3) return word
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`
  if (/(ss|x|z|ch|sh)es$/.test(word)) return word.slice(0, -2)
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1)
  return word
}

/** Lower-case words of 3 or more letters, without common words. */
export function searchTerms(text: string): string[] {
  return [...new Set((text.toLowerCase().match(/[a-z0-9]{3,}/g) || []).filter((word) => !STOP_WORDS.has(word)).map(singular))]
}

/**
 * How well a section answers a question: each question word found in the
 * title counts 3, in the body 1. Zero means no match. Used for the general
 * Ask a Question box (Rod: "search the workspace's published sections").
 */
export function uatNoteScore(question: string, note: { title: string; text: string }): number {
  const terms = searchTerms(question)
  if (!terms.length) return 0
  const title = searchTerms(note.title)
  const body = new Set(searchTerms(note.text))
  let score = 0
  for (const term of terms) {
    if (title.includes(term)) score += 3
    else if (body.has(term)) score += 1
  }
  return score
}

/** The best matching sections, best first, at most `limit`. Sections with no match are left out. */
export function bestUatNotes<T extends { title: string; text: string }>(question: string, notes: T[], limit: number): T[] {
  return notes
    .map((note, index) => ({ note, index, score: uatNoteScore(question, note) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map((entry) => entry.note)
}
